import type { SessionService, SessionState } from '@efdigital/ll-addon-auth';
import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { Await } from '@warp-drive/ember';

interface UserProfileButtonSignature {
  Args: {
    /** When set, the profile button links to this route (e.g. settings). */
    route?: string;
    /**
     * Profile photo to show instead of initials. The session only knows a
     * display name, so a photo has to come from whatever the caller holds — a
     * coach's published profile, for instance.
     */
    avatarUrl?: string | null;
  };
  Element: HTMLElement;
}

function initials(state: SessionState) {
  const letters =
    state.displayName
      ?.split(' ')
      .map((name) => name[0])
      .join('')
      .toUpperCase() || '';
  if (letters.length > 2) {
    return letters[0]! + letters[letters.length - 1]!;
  }
  if (letters.length === 0) {
    return '@';
  }
  return letters;
}

const ProfileContent: TOC<{
  Args: { user: SessionState; avatarUrl?: string | null };
}> = <template>
  {{#if @avatarUrl}}
    <img
      src={{@avatarUrl}}
      alt=""
      class="ll-avatar-xs rounded-circle object-fit-cover flex-shrink-0"
    />
  {{else}}
    <span
      class="ll-avatar-xs rounded-circle text-bg-primary d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0"
      aria-hidden="true"
    >{{initials @user}}</span>
  {{/if}}
  <span class="min-w-0">
    <span class="d-block fw-bold text-truncate">{{@user.displayName}}</span>
    {{#if @user.client.title}}
      <span
        class="d-block text-body-secondary text-truncate small"
      >{{@user.client.title}}</span>
    {{/if}}
  </span>
</template>;

export default class UserProfileButton extends Component<UserProfileButtonSignature> {
  @service declare session: SessionService;

  get user() {
    return this.session.getCurrent();
  }

  <template>
    <Await @promise={{this.user}}>
      <:success as |user|>
        {{#if @route}}
          <LinkTo
            @route={{@route}}
            class="d-flex align-items-center gap-3 border-0 border-top bg-transparent text-start text-reset text-decoration-none px-3 py-3 w-100"
            ...attributes
          >
            <ProfileContent @user={{user}} @avatarUrl={{@avatarUrl}} />
          </LinkTo>
        {{else}}
          <div
            class="d-flex align-items-center gap-3 border-0 border-top bg-transparent text-start px-3 py-3 w-100"
            ...attributes
          >
            <ProfileContent @user={{user}} @avatarUrl={{@avatarUrl}} />
          </div>
        {{/if}}
      </:success>
    </Await>
  </template>
}
